// Product Attributes dashboard: header (title + stats + "+ Type") / left rail
// (types, ~280px) / main panel. The main panel is one of three mutually
// exclusive views — management (selected type's values table), cross-type
// search results, or the read-only category-lens preview — driven by the
// search input + "Preview as category…" toggle above it. Route/sidebar/flag
// are unchanged from the earlier cycle-1 master-detail page this replaces —
// see docs/superpowers/plans/2026-07-19-attribute-dictionary-dashboard.md.

import { Icon } from '@chakra-ui/react';
import {
  Box,
  Flex,
  IconButtonWithTooltip,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
} from '@elkaso-app/web-design';
import { useGetAttributeTypesApi, useGetAttributeUsageApi } from 'apis/product-attributes';
import type { CategoryRef } from 'pages/exclusive-deals/components/editable-deals-grid/editable-deal-row.types';
import { useEffect, useMemo, useState } from 'react';
import { FiSearch, FiX } from 'react-icons/fi';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';

import { AttributeDictionaryHeader } from '../components/product-attributes/attribute-dictionary-header';
import { searchAttributeValues } from '../components/product-attributes/attribute-search';
import { AttributeSearchResults } from '../components/product-attributes/attribute-search-results';
import { AttributeTypesRail } from '../components/product-attributes/attribute-types-rail';
import { AttributeValuesTable } from '../components/product-attributes/attribute-values-table';
import { CategoryLensPreview, CategoryLensToggle } from '../components/product-attributes/category-lens';

export const ProductAttributesScreen = () => {
  const dispatch = useAppDispatch();
  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const [isCreateTypeOpen, setIsCreateTypeOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [lensCategory, setLensCategory] = useState<CategoryRef | null>(null);

  const { data, isLoading } = useGetAttributeTypesApi();
  const { data: usageData } = useGetAttributeUsageApi();
  const types = data?.data ?? [];
  const usage = usageData?.data;
  const selectedType = types.find((t) => t.id === selectedTypeId) ?? null;

  // Category-bindings cache for TYPES only — populated as CategoryBindingsModal
  // is opened/saved for a given type (see category-bindings-modal.tsx). The
  // types list now carries `boundCategoryIds` per type too (same as values), so
  // this cache is just the freshness override between a modal save and the list
  // refetch, plus the fallback path while an older BE omits the field.
  const [typeCategoryCache, setTypeCategoryCache] = useState<Record<number, number[]>>({});

  const isSearching = searchQuery.trim().length > 0;
  const searchHits = useMemo(
    () => (isSearching ? searchAttributeValues(types, searchQuery) : []),
    [types, searchQuery, isSearching]
  );
  const matchingTypeIds = useMemo(() => new Set(searchHits.map((hit) => hit.type.id)), [searchHits]);

  useEffect(() => {
    dispatch(changeTitle('Product Attributes'));
  }, [dispatch]);

  // Selecting a type from the rail always returns to the management view —
  // clearing the search restores the selection this way rather than the
  // other way around, since a rail click is an explicit "manage this type" intent.
  const selectType = (id: number) => {
    setSelectedTypeId(id);
    setSearchQuery('');
  };

  return (
    <Box p='4'>
      <AttributeDictionaryHeader types={types} usage={usage} onAddType={() => setIsCreateTypeOpen(true)} />

      <Flex gap='4' align='flex-start'>
        <AttributeTypesRail
          types={types}
          isLoading={isLoading}
          selectedTypeId={selectedTypeId}
          onSelect={selectType}
          typeCategoryCache={typeCategoryCache}
          onTypeBindingsLoaded={(typeId, ids) => setTypeCategoryCache((prev) => ({ ...prev, [typeId]: ids }))}
          isCreateOpen={isCreateTypeOpen}
          onCloseCreate={() => setIsCreateTypeOpen(false)}
          matchingTypeIds={isSearching ? matchingTypeIds : undefined}
        />

        <Box flex='1' minW='0'>
          <Flex align='center' gap='3' mb='4' wrap='wrap'>
            <InputGroup maxW='320px' size='sm'>
              <InputLeftElement pointerEvents='none'>
                <Icon as={FiSearch} color='gray.400' />
              </InputLeftElement>
              <Input
                placeholder='Search values across all types…'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                isDisabled={!!lensCategory}
                borderRadius='md'
              />
              {isSearching && (
                <InputRightElement>
                  <IconButtonWithTooltip
                    size='xs'
                    label='Clear search'
                    aria-label='clear-search-btn'
                    Icon={<Icon as={FiX} />}
                    variant='unstyled'
                    onClick={() => setSearchQuery('')}
                  />
                </InputRightElement>
              )}
            </InputGroup>

            <CategoryLensToggle value={lensCategory} onChange={setLensCategory} isDisabled={isSearching} />
          </Flex>

          {lensCategory ? (
            <CategoryLensPreview category={lensCategory} />
          ) : isSearching ? (
            <AttributeSearchResults hits={searchHits} usage={usage} />
          ) : selectedType ? (
            // Remount per type resets localOrder — same keying rationale as
            // the workspace grid (see family-workspace's variants grid).
            <AttributeValuesTable key={selectedType.id} type={selectedType} usage={usage} />
          ) : (
            <Text color='gray.500' fontSize='sm' mt='10' textAlign='center'>
              Select an attribute type to manage its values.
            </Text>
          )}
        </Box>
      </Flex>
    </Box>
  );
};
